import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { RotateCcw } from 'lucide-react';
import { useTranslation } from '../../../i18n';
import useEditorAutoGrow from './useEditorAutoGrow';

const shuffleWithSeed = (array, seed) => {
  const result = [...array];
  const seededRandom = (s) => {
    const x = Math.sin(s) * 10000;
    return x - Math.floor(x);
  };
  let seedValue = seed;
  for (let i = result.length - 1; i > 0; i--) {
    seedValue++;
    const j = Math.floor(seededRandom(seedValue) * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
};

// Parse text with {{slotId}} markers into segments
// Returns array of { type: 'text', value } or { type: 'slot', slotId }
const parseTextSegments = (text) => {
  if (!text) return [];
  const segments = [];
  const regex = /\{\{([^{}\n\r]+?)\}\}/g;
  let lastIndex = 0;
  let m;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > lastIndex) {
      segments.push({ type: 'text', value: text.slice(lastIndex, m.index) });
    }
    segments.push({ type: 'slot', slotId: m[1] });
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    segments.push({ type: 'text', value: text.slice(lastIndex) });
  }
  return segments;
};

const DragLabelTextElement = ({
  element,
  isEditor = false,
  studentView = false,
  placements: externalPlacements = null,
  showResult = false,
  perSlotResults = null,
  onPlacementsChange,
  onCheck,
  onRetry,
  disabled = false,
  hideCheckButton = false,
  onAutoGrow,
}) => {
  const { t } = useTranslation();
  const { content, style, id } = element;
  const {
    text = '',
    slots = [],
    labels = [],
    paletteSide = 'top',
    allowRetry = false,
    textColor = '#1f2937',
    fontSize = 18,
    fontFamily = 'Inter',
    labelColor = '#ffffff',
    labelTextColor = '#1f2937',
    slotColor = '#2A4D25',
    paletteColor = '#f9fafb',
  } = content || {};
  const backgroundColor = style?.backgroundColor || '#ffffff';

  const segments = useMemo(() => parseTextSegments(text), [text]);

  const shuffledLabels = useMemo(() => {
    if (!labels || labels.length === 0) return [];
    const seed = id ? id.split('').reduce((a, c) => a + c.charCodeAt(0), 0) : 1;
    return shuffleWithSeed(labels, seed);
  }, [labels, id]);

  const slotMinWidth = useMemo(() => {
    if (!labels || labels.length === 0) return 80;
    if (typeof document === 'undefined') {
      const longest = labels.reduce((m, l) => Math.max(m, (l?.text || '').length), 0);
      return Math.max(80, Math.round(longest * fontSize * 0.55) + 28);
    }
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.font = `500 ${fontSize - 2}px "${fontFamily}", Inter, system-ui, sans-serif`;
    let maxW = 0;
    labels.forEach((l) => {
      const w = ctx.measureText(l?.text || '').width;
      if (w > maxW) maxW = w;
    });
    return Math.max(80, Math.ceil(maxW) + 32);
  }, [labels, fontSize, fontFamily]);

  const [placements, setPlacements] = useState(externalPlacements || {});
  useEffect(() => {
    if (externalPlacements) setPlacements(externalPlacements);
  }, [externalPlacements]);

  const [draggedLabelId, setDraggedLabelId] = useState(null);
  const [dragOverSlotId, setDragOverSlotId] = useState(null);
  // Touch/pen drag state — HTML5 drag events do not fire on mobile browsers,
  // so we run a parallel pointer-based drag with a floating ghost element and
  // elementFromPoint hit-testing to find the target slot.
  const pointerDragRef = useRef(null); // { labelId, ghostEl, pointerId }
  const scrollRef = useRef(null);
  useEditorAutoGrow({
    enabled: isEditor,
    scrollRef,
    currentHeightPct: element.size?.height || 0,
    contentCount: slots.length + labels.length,
    onAutoGrow,
  });

  const placedLabelIds = useMemo(() => new Set(Object.values(placements).filter(Boolean)), [placements]);
  const allPlaced = !isEditor && shuffledLabels.length > 0 && shuffledLabels.every((l) => placedLabelIds.has(l.id));

  const updatePlacements = useCallback((next) => {
    setPlacements(next);
    onPlacementsChange?.(next);
  }, [onPlacementsChange]);

  const handleDragStart = (e, labelId) => {
    if (disabled || showResult || isEditor) return;
    // Order matters in Safari: setData must come before effectAllowed
    e.dataTransfer.setData('text/plain', labelId);
    e.dataTransfer.effectAllowed = 'move';

    // Safari can't render a drag image when the dragged element lives inside a
    // `transform: scale(...)` subtree (the slide is rendered scaled), and the
    // drag visually never starts. Render a clone on document.body and use it
    // as the drag image so WebKit has something to attach the pointer to.
    const label = labels.find((l) => l.id === labelId);
    if (label && typeof document !== 'undefined' && e.dataTransfer.setDragImage) {
      const ghost = document.createElement('div');
      ghost.textContent = label.text;
      Object.assign(ghost.style, {
        position: 'fixed',
        top: '-1000px',
        left: '-1000px',
        padding: '4px 12px',
        borderRadius: '9999px',
        border: `2px solid ${slotColor}`,
        background: labelColor,
        color: labelTextColor,
        font: `500 ${fontSize - 2}px "${fontFamily}", Inter, system-ui, sans-serif`,
        whiteSpace: 'nowrap',
        pointerEvents: 'none',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      });
      document.body.appendChild(ghost);
      const rect = ghost.getBoundingClientRect();
      e.dataTransfer.setDragImage(ghost, rect.width / 2, rect.height / 2);
      setTimeout(() => { ghost.remove(); }, 0);
    }

    setDraggedLabelId(labelId);
  };

  const handleDragEnd = () => {
    setDraggedLabelId(null);
    setDragOverSlotId(null);
  };

  const handleSlotDragOver = (e, slotId) => {
    if (disabled || showResult || isEditor) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverSlotId(slotId);
  };

  const handleSlotDragLeave = () => setDragOverSlotId(null);

  const handleSlotDrop = (e, slotId) => {
    if (disabled || showResult || isEditor) return;
    e.preventDefault();
    const labelId = draggedLabelId || e.dataTransfer.getData('text/plain');
    if (!labelId) return;
    const next = { ...placements };
    Object.keys(next).forEach((sid) => {
      if (next[sid] === labelId) next[sid] = null;
    });
    next[slotId] = labelId;
    updatePlacements(next);
    setDraggedLabelId(null);
    setDragOverSlotId(null);
  };

  const handlePlacedClick = (slotId) => {
    if (disabled || showResult || isEditor) return;
    if (!placements[slotId]) return;
    const next = { ...placements };
    next[slotId] = null;
    updatePlacements(next);
  };

  // Start a pointer-based drag for touch/pen input. Mouse still uses native
  // HTML5 drag so we don't regress desktop behavior.
  const handlePointerDown = useCallback((e, labelId) => {
    if (disabled || showResult || isEditor) return;
    if (e.pointerType === 'mouse') return;
    if (pointerDragRef.current) return;
    const label = labels.find((l) => l.id === labelId);
    if (!label) return;
    e.preventDefault();

    const ghost = document.createElement('div');
    ghost.textContent = label.text;
    Object.assign(ghost.style, {
      position: 'fixed',
      left: '0',
      top: '0',
      padding: '4px 12px',
      borderRadius: '9999px',
      border: `2px solid ${slotColor}`,
      background: labelColor,
      color: labelTextColor,
      font: `500 ${fontSize - 2}px "${fontFamily}", Inter, system-ui, sans-serif`,
      whiteSpace: 'nowrap',
      pointerEvents: 'none',
      boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
      zIndex: '99999',
      opacity: '0.95',
      userSelect: 'none',
      WebkitUserSelect: 'none',
      willChange: 'transform',
    });
    document.body.appendChild(ghost);
    const rect = ghost.getBoundingClientRect();
    ghost.style.transform = `translate(${e.clientX - rect.width / 2}px, ${e.clientY - rect.height / 2}px)`;

    pointerDragRef.current = { labelId, ghostEl: ghost, pointerId: e.pointerId };
    setDraggedLabelId(labelId);
  }, [disabled, showResult, isEditor, labels, slotColor, labelColor, labelTextColor, fontFamily, fontSize]);

  // Global pointermove/up while a touch drag is active. Re-attached whenever
  // placements/updatePlacements change so the drop handler always sees the
  // latest state — synchronous cleanup means no events are dropped mid-drag.
  useEffect(() => {
    const findSlotIdAt = (clientX, clientY) => {
      const drag = pointerDragRef.current;
      if (!drag) return null;
      const prevDisplay = drag.ghostEl.style.display;
      drag.ghostEl.style.display = 'none';
      const el = typeof document !== 'undefined' ? document.elementFromPoint(clientX, clientY) : null;
      drag.ghostEl.style.display = prevDisplay;
      const slotEl = el && el.closest ? el.closest('[data-drag-slot-id]') : null;
      return slotEl ? slotEl.getAttribute('data-drag-slot-id') : null;
    };

    const cleanupDrag = () => {
      const drag = pointerDragRef.current;
      if (!drag) return;
      if (drag.ghostEl && drag.ghostEl.parentNode) drag.ghostEl.parentNode.removeChild(drag.ghostEl);
      pointerDragRef.current = null;
      setDraggedLabelId(null);
      setDragOverSlotId(null);
    };

    const onMove = (e) => {
      const drag = pointerDragRef.current;
      if (!drag || e.pointerId !== drag.pointerId) return;
      e.preventDefault();
      const rect = drag.ghostEl.getBoundingClientRect();
      drag.ghostEl.style.transform = `translate(${e.clientX - rect.width / 2}px, ${e.clientY - rect.height / 2}px)`;
      setDragOverSlotId(findSlotIdAt(e.clientX, e.clientY));
    };

    const onUp = (e) => {
      const drag = pointerDragRef.current;
      if (!drag || e.pointerId !== drag.pointerId) return;
      const slotId = findSlotIdAt(e.clientX, e.clientY);
      const labelId = drag.labelId;
      cleanupDrag();
      if (slotId) {
        const next = { ...placements };
        Object.keys(next).forEach((sid) => {
          if (next[sid] === labelId) next[sid] = null;
        });
        next[slotId] = labelId;
        updatePlacements(next);
      }
    };

    const onCancel = (e) => {
      const drag = pointerDragRef.current;
      if (!drag) return;
      if (e && e.pointerId != null && e.pointerId !== drag.pointerId) return;
      cleanupDrag();
    };

    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
    };
  }, [placements, updatePlacements]);

  // Ensure ghost is removed if the component unmounts mid-drag.
  useEffect(() => () => {
    const drag = pointerDragRef.current;
    if (drag && drag.ghostEl && drag.ghostEl.parentNode) {
      drag.ghostEl.parentNode.removeChild(drag.ghostEl);
    }
    pointerDragRef.current = null;
  }, []);

  const handleCheck = () => onCheck?.(placements);
  const handleRetry = () => {
    setPlacements({});
    onRetry?.();
  };

  const allSlotsFilled = slots.length > 0 && slots.every((s) => placements[s.id]);

  const isHorizontalPalette = paletteSide === 'top' || paletteSide === 'bottom';
  const flexDirection = paletteSide === 'top' ? 'column'
    : paletteSide === 'bottom' ? 'column-reverse'
    : paletteSide === 'left' ? 'row'
    : 'row-reverse';

  const renderLabelPill = (label, opts = {}) => {
    const { isDraggable = true, isInSlot = false, slotResult = null, slotId = null } = opts;
    let bg = labelColor;
    let color = labelTextColor;
    let borderColor = slotColor;
    if (isInSlot && showResult && slotResult !== null) {
      if (slotResult === true) {
        bg = '#dcfce7'; color = '#166534'; borderColor = '#22c55e';
      } else if (slotResult === false) {
        bg = '#fee2e2'; color = '#991b1b'; borderColor = '#ef4444';
      }
    }
    const dragEnabled = isDraggable && !disabled && !showResult && !isEditor;
    const touchDragEnabled = dragEnabled && !isInSlot;
    // In-slot pill lives inside a <p>, so it must be inline (<span>).
    // Palette pill can be a <div>; iOS Safari doesn't reliably fire pointerdown
    // on a <span draggable> (it prefers to start a text-drag selection), which
    // is why touch drag from the palette silently no-ops on phones.
    const Tag = isInSlot ? 'span' : 'div';
    return (
      <Tag
        key={label.id + (isInSlot ? '_in_' + slotId : '_pal')}
        draggable={dragEnabled}
        onDragStart={(e) => handleDragStart(e, label.id)}
        onDragEnd={handleDragEnd}
        onPointerDown={touchDragEnabled ? (e) => handlePointerDown(e, label.id) : undefined}
        onClick={isInSlot ? () => handlePlacedClick(slotId) : undefined}
        className={`
          inline-flex items-center justify-center px-3 py-1 mx-1 rounded-full
          font-medium border-2 whitespace-nowrap align-middle
          ${dragEnabled ? 'cursor-grab active:cursor-grabbing' : ''}
          ${isInSlot && !showResult && !disabled && !isEditor ? 'cursor-pointer hover:bg-gray-50' : ''}
          ${draggedLabelId === label.id ? 'opacity-40' : ''}
          transition-colors duration-100
        `}
        style={{
          backgroundColor: bg,
          color,
          borderColor,
          fontSize: `${fontSize - 2}px`,
          fontFamily,
          height: `${fontSize + 12}px`,
          minWidth: isInSlot ? `${slotMinWidth}px` : undefined,
          WebkitUserDrag: dragEnabled ? 'element' : 'none',
          WebkitUserSelect: 'none',
          userSelect: 'none',
          // Prevents the browser from claiming touch gestures (scroll/zoom)
          // before pointermove reaches us on mobile.
          touchAction: touchDragEnabled ? 'none' : undefined,
        }}
      >
        {label.text}
      </Tag>
    );
  };

  const renderSlot = (slotId) => {
    const placedLabelId = placements[slotId];
    const slotResult = showResult && perSlotResults ? perSlotResults[slotId] : null;
    const isOver = dragOverSlotId === slotId;

    if (placedLabelId) {
      const lbl = labels.find((l) => l.id === placedLabelId) || { id: placedLabelId, text: '' };
      return renderLabelPill(lbl, { isDraggable: false, isInSlot: true, slotResult, slotId });
    }
    return (
      <span
        key={slotId}
        data-drag-slot-id={!isEditor && !showResult ? slotId : undefined}
        onDragOver={(e) => handleSlotDragOver(e, slotId)}
        onDragLeave={handleSlotDragLeave}
        onDrop={(e) => handleSlotDrop(e, slotId)}
        className="inline-block mx-1 rounded-full border-2 border-dashed align-middle transition-colors"
        style={{
          minWidth: `${slotMinWidth}px`,
          height: `${fontSize + 12}px`,
          borderColor: slotResult === true ? '#22c55e' : slotResult === false ? '#ef4444' : slotColor,
          backgroundColor: isOver ? 'rgba(42, 77, 37, 0.18)' : 'rgba(255,255,255,0.6)',
        }}
      />
    );
  };

  const hideChrome = !isEditor;

  return (
    <div
      className={`w-full h-full rounded-xl overflow-hidden flex relative ${
        hideChrome ? '' : 'border-2 shadow-sm'
      }`}
      style={{
        backgroundColor: hideChrome ? 'transparent' : backgroundColor,
        flexDirection,
        borderColor: '#e5e7eb',
      }}
    >
      {/* Palette — stays visible after check so the surrounding layout
          doesn't shift. */}
      <div
        className={`relative flex ${isHorizontalPalette ? 'flex-row flex-wrap' : 'flex-col'} gap-2 p-3 ${isHorizontalPalette ? '' : 'min-w-[120px]'}`}
        style={{
          alignItems: 'center',
          justifyContent: isHorizontalPalette ? 'center' : 'flex-start',
          backgroundColor: paletteColor,
        }}
      >
        {shuffledLabels.map((label) => {
          const isPlaced = !isEditor && placedLabelIds.has(label.id);
          return (
            <div
              key={label.id}
              style={{
                visibility: isPlaced ? 'hidden' : 'visible',
                pointerEvents: isPlaced ? 'none' : undefined,
              }}
            >
              {renderLabelPill(label)}
            </div>
          );
        })}
        {allPlaced && (
          <span className="absolute inset-0 flex items-center justify-center text-xs text-gray-400 pointer-events-none">
            {t('games.dragLabel.allLabelsPlaced')}
          </span>
        )}
      </div>

      {/* Text area */}
      <div ref={scrollRef} className="flex-1 min-h-0 min-w-0 p-5 overflow-y-auto overflow-x-hidden">
        {segments.length === 0 ? (
          <p className="text-sm text-gray-400">{t('games.fillGaps.enterTextInSettings')}</p>
        ) : (
          <p style={{ fontSize: `${fontSize}px`, fontFamily, lineHeight: 1.9, color: textColor, whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>
            {segments.map((seg, idx) =>
              seg.type === 'text'
                ? <span key={`t_${idx}`}>{seg.value}</span>
                : <React.Fragment key={`s_${seg.slotId}_${idx}`}>{renderSlot(seg.slotId)}</React.Fragment>
            )}
          </p>
        )}
      </div>

      {/* Action bar */}
      {!isEditor && (
        <div className="absolute bottom-2 right-2 z-10 flex items-center gap-2">
          {showResult && allowRetry && perSlotResults && !Object.values(perSlotResults).every(v => v === true) && (
            <button
              onClick={handleRetry}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg shadow-sm transition"
            >
              <RotateCcw size={14} />
              {t('games.common.tryAgain')}
            </button>
          )}
          {!showResult && !hideCheckButton && (
            <button
              onClick={handleCheck}
              disabled={!allSlotsFilled}
              className={`px-4 py-1.5 text-sm font-medium rounded-lg shadow-sm transition ${
                allSlotsFilled
                  ? 'bg-indigo-500 text-white hover:bg-indigo-600'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {t('games.common.check')}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default DragLabelTextElement;
